import React, { useMemo, useState, useCallback } from 'react';
import NONE_OPTION_KEY from '~/modules/common/enums/NoneOptionKey';

export const cleanSearchTerm = value => value.replace(/['",]+/g, '');

export const mapOption = option => ({
  ...option,
  value: option.id,
  label: option.displayText || option.name,
  key: option.key || option.displayText || option.name,
  displayText: option.displayText || option.name
});

export const mapOptions = options => options.map(mapOption);

export const matchOption = fetchOptions => async (_, term) => {
  if (!term) {
    return null;
  }

  const cleanedTerm = cleanSearchTerm(term);
  const options = await fetchOptions(cleanedTerm);

  let option;

  // Handle NONE option (added client-side)
  if (term === NONE_OPTION_KEY) {
    option = options.shift();
  } else {
    const filteredOptions = options.filter(r => r.value !== NONE_OPTION_KEY);

    // Find exact match, otherwise first option
    option =
      filteredOptions.find(
        opt =>
          opt.displayText === cleanedTerm ||
          opt.label === cleanedTerm ||
          opt.key === cleanedTerm
      ) || filteredOptions[0];
  }

  if (option) {
    return {
      ...option,
      key: option.key || option.displayText || option.name
    };
  }

  return null;
};

export const withCustomFetchCriteriaProvider = (
  tag,
  context,
  useFetchOptions
) => BaseComponent => props => {
  const { searchCriteria: { criterions = {} } = {} } = props;
  const [selected, setSelected] = useState(criterions[tag] || []);
  const { fetchOptions } = useFetchOptions(props);
  const $fetchOptions = useCallback(
    async searchTerm => mapOptions(await fetchOptions(searchTerm)),
    [fetchOptions]
  );

  const value = useMemo(
    () => ({
      options: mapOptions([]),
      matchOption: matchOption($fetchOptions),
      selected,
      setSelected,
      fetchOptions: $fetchOptions,
      suggestions: $fetchOptions
    }),
    [$fetchOptions, selected]
  );

  return (
    <context.Provider value={value}>
      <BaseComponent {...props} />
    </context.Provider>
  );
};

export default withCustomFetchCriteriaProvider;
